import { inject, Injectable } from '@angular/core';
import { AppLauncher, CanOpenURLResult } from '@capacitor/app-launcher';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';

import { APP_CONFIG } from '@smartcharge/shared/index';

const SOURCE_HOST = 'app.smartcharge.io';
const TARGET_HOST = 'app.current.eco';

@Injectable({
  providedIn: 'root'
})
export class TransferSessionService {
  private readonly config = inject(APP_CONFIG);
  private readonly platform = inject(Platform);
  private readonly storage = inject(Storage);

  canTransferFrom() {
    return this.config.appHost === SOURCE_HOST;
  }

  canTransferTo() {
    return this.config.appHost === 'app.current.eco';
  }

  isSourceAppInstalled(): Promise<CanOpenURLResult> {
    if (this.platform.is('capacitor') === false) {
      return Promise.resolve({ value: true });
    }

    const url = this.platform.is('ios') ? 'smartcharge://' : 'no.meshcrafts.SmartChargeEV';

    return AppLauncher.canOpenUrl({ url });
  }

  isTargetAppInstalled(): Promise<CanOpenURLResult> {
    if (this.platform.is('capacitor') === false) {
      return Promise.resolve({ value: true });
    }

    const url = this.platform.is('ios') ? 'current://' : 'eco.current';

    return AppLauncher.canOpenUrl({ url });
  }

  async startMigration() {
    const url = `${this.getSourceAppHost()}/stations?transferSession=true`;

    AppLauncher.openUrl({ url });
  }

  async transferSession(transferToken: string) {
    try {
      const data = atob(transferToken);
      const { appid, rToken, token } = JSON.parse(data);

      await this.storage.set('appid', appid);
      await this.storage.set('rToken', rToken);
      await this.storage.set('token', token);
    } catch (e) {
      console.error(e);
    }
  }

  async transferSessionRedirect() {
    let url = `${this.getTargetAppHost()}/stations`;

    const token = await this.generateToken();

    if (token.length) {
      url += `?transferToken=${token}`;
    }

    AppLauncher.openUrl({ url });
  }

  private async generateToken(): Promise<string> {
    const appid = await this.storage.get('appid');
    const rToken = await this.storage.get('rToken');
    const token = await this.storage.get('token');

    if (!token) {
      return '';
    }

    const data = JSON.stringify({ appid, rToken, token });

    return btoa(data);
  }

  private getAppHost(host: string) {
    return `https://${host}`;
  }

  private getSourceAppHost() {
    if (this.platform.is('capacitor') && this.platform.is('ios')) {
      return `smartcharge:/`;
    }

    return this.getAppHost(SOURCE_HOST);
  }

  private getTargetAppHost() {
    if (this.platform.is('capacitor') && this.platform.is('ios')) {
      return `current:/`;
    }
    return this.getAppHost(TARGET_HOST);
  }
}
